import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Clock, AlertTriangle, CheckCircle, TrendingUp,
    Award, Flag, BarChart3, User, Users2, X, Star,
    MessageSquare, Calendar
} from 'lucide-react';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, 
    Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { googleAPI } from '../../utils/api';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import './PerformanceMetrics.scss';
import { anonymizeName, anonymizeCompanyName } from '../../utils/demoMode.js';

const PerformanceMetrics = () => {
    const { userRole, isAdmin, isManager } = useAuth();
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [timeRange, setTimeRange] = useState('30');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedTechnicianId, setSelectedTechnicianId] = useState('');
    const [viewMode, setViewMode] = useState(isAdmin || isManager ? 'admin' : 'technician');
    const [managers, setManagers] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [loadingManagers, setLoadingManagers] = useState(true);
    const [loadingTechnicians, setLoadingTechnicians] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);

    useEffect(() => {
        // Reset filters based on initial view mode
        if (viewMode === 'admin') {
            setSelectedTechnicianId('');
        } else {
            setSelectedUserId('');
        }
        
        if (isAdmin || isManager) {
            loadManagers();
        }
        loadTechnicians();
    }, []);

    useEffect(() => {
        loadMetrics();
    }, [timeRange, selectedUserId, selectedTechnicianId, viewMode]);

    const loadManagers = async () => {
        try {
            setLoadingManagers(true);
            const result = await googleAPI.getManagers();
            if (result.success) {
                setManagers(result.data);
            }
        } catch (err) {
            console.error('Error loading managers:', err);
        } finally {
            setLoadingManagers(false);
        }
    };

    const loadTechnicians = async () => {
        try {
            setLoadingTechnicians(true);
            const result = await googleAPI.getTechnicians();
            if (result.success) {
                setTechnicians(result.data);
            }
        } catch (err) {
            console.error('Error loading technicians:', err);
        } finally {
            setLoadingTechnicians(false);
        }
    };

    const loadMetrics = async () => {
        try {
            setLoading(true);
            setError('');
            
            let result;
            if (viewMode === 'admin') {
                result = await googleAPI.getPerformanceMetrics(timeRange, selectedUserId || null);
            } else {
                result = await googleAPI.getTechnicianPerformanceMetrics(timeRange, selectedTechnicianId || null);
            }
            
            if (result.success) {
                setMetrics(result.data);
            } else {
                setError(result.message || 'Failed to load performance metrics');
            }
        } catch (err) {
            setError(err.message || 'Failed to load performance metrics');
            console.error('Error loading performance metrics:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewModeChange = (newViewMode) => {
        setViewMode(newViewMode);
        // Reset filters when switching view modes
        if (newViewMode === 'admin') {
            setSelectedTechnicianId(''); // Clear technician selection
        } else {
            setSelectedUserId(''); // Clear manager selection
        }
    };

    const formatHours = (hours) => {
        if (hours < 1) {
            return `${Math.round(hours * 60)} min`;
        } else if (hours < 24) {
            return `${Math.round(hours * 10) / 10} hrs`;
        } else {
            const days = Math.floor(hours / 24);
            const remainingHours = Math.round(hours % 24);
            return `${days}d ${remainingHours}h`;
        }
    };

    const getPerformanceColor = (rate) => {
        if (rate >= 80) return 'excellent';
        if (rate >= 60) return 'good';
        if (rate >= 40) return 'fair';
        return 'poor';
    };

    const handleReviewClick = async (reviewId) => {
        try {
            // Fetch full review details
            const result = await googleAPI.getReview(reviewId);
            if (result.success) {
                setSelectedReview(result.data);
                setShowReviewModal(true);
            }
        } catch (error) {
            console.error('Error fetching review details:', error);
        }
    };

    const closeReviewModal = () => {
        setShowReviewModal(false);
        setSelectedReview(null);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="performance-metrics">
                <motion.div
                    className="config-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="header-content">
                        <div className="logo-section">
                            <TrendingUp className="logo" />
                            <div>
                                <h1>Performance Metrics</h1>
                                <p>Track and analyze team performance, approval times, and review metrics</p>
                            </div>
                        </div>
                    </div>
                </motion.div>
                <div className="error-message">
                    <AlertTriangle />
                    <p>{error}</p>
                    <button onClick={loadMetrics} className="btn btn-primary">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!metrics) {
        return (
            <div className="performance-metrics">
                <motion.div
                    className="config-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="header-content">
                        <div className="logo-section">
                            <TrendingUp className="logo" />
                            <div>
                                <h1>Performance Metrics</h1>
                                <p>Track and analyze team performance, approval times, and review metrics</p>
                            </div>
                        </div>
                    </div>
                </motion.div>
                <div className="empty-state">
                    <p>No metrics data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="performance-metrics">
            <motion.div
                className="config-header"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="header-content">
                    <div className="logo-section">
                        <TrendingUp className="logo" />
                        <div>
                            <h1>Performance Metrics</h1>
                            <p>Track and analyze team performance, approval times, and review metrics</p>
                        </div>
                    </div>
                </div>
            </motion.div>

            <motion.div
                className="filters-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
            >
                <div className="filters-container">
                    {(isAdmin || isManager) && (
                        <div className="filter-group">
                            <label>View:</label>
                            <select
                                value={viewMode}
                                onChange={(e) => handleViewModeChange(e.target.value)}
                                className="filter-select"
                            >
                                <option value="admin">Admin Overview</option>
                                <option value="technician">Technician Performance</option>
                            </select>
                        </div>
                    )}

                    {viewMode === 'admin' && (
                        <div className="filter-group">
                            <label>Manager:</label>
                            <select
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                className="filter-select"
                                disabled={loadingManagers}
                            >
                                <option value="">All Managers</option>
                                {managers.map(manager => (
                                    <option key={manager.id} value={manager.id}>
                                        {manager.name} ({manager.userRole})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {viewMode === 'technician' && (isAdmin || isManager) && (
                        <div className="filter-group">
                            <label>Technician:</label>
                            <select
                                value={selectedTechnicianId}
                                onChange={(e) => setSelectedTechnicianId(e.target.value)}
                                className="filter-select"
                                disabled={loadingTechnicians}
                            >
                                <option value="">My Performance</option>
                                {technicians.map(technician => (
                                    <option key={technician.id} value={technician.id}>
                                        {technician.name} ({technician.crmCode})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="filter-group">
                        <label>Time Range:</label>
                        <select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            className="filter-select"
                        >
                            <option value="7">Last 7 days</option>
                            <option value="30">Last 30 days</option>
                            <option value="90">Last 90 days</option>
                        </select>
                    </div>
                </div>
            </motion.div>

            {/* Admin View - Performance Metrics */}
            {viewMode === 'admin' && metrics && (
                <div className="metrics-grid">
                    {/* Approval Speed Metrics */}
                <div className="metrics-card approval-metrics">
                    <div className="card-header">
                        <Clock className="card-icon" />
                        <h3>Approval Speed</h3>
                    </div>
                    <div className="card-content">
                        <div className="stat-row">
                            <span className="stat-label">Total Approved:</span>
                            <span className="stat-value">{metrics?.approvalMetrics?.totalApproved || 0}</span>
                        </div>
                        <div className="stat-row">
                            <span className="stat-label">Average Time:</span>
                            <span className="stat-value">
                                {formatHours(metrics?.approvalMetrics?.averageApprovalTimeHours || 0)}
                            </span>
                        </div>
                        <div className="stat-row">

                            <span className="stat-label">Fast Approvals (≤2h):</span>
                            <span className="stat-value highlight-good">
                                {metrics?.approvalMetrics?.fastApprovals || 0}
                            </span>
                        </div>
                        <div className="stat-row">
                            <span className="stat-label">Slow Approvals (&gt;24h):</span>
                            <span className="stat-value highlight-warning">
                                {metrics?.approvalMetrics?.slowApprovals || 0}
                            </span>
                        </div>
                        <div className="performance-indicator">
                            <span className="indicator-label">Fast Approval Rate:</span>
                            <div className={`performance-rate ${getPerformanceColor(metrics?.approvalMetrics?.fastApprovalRate || 0)}`}>
                                {metrics?.approvalMetrics?.fastApprovalRate || 0}%
                            </div>
                        </div>
                    </div>
                </div>

                {/* Flagged Reviews Metrics */}
                <div className="metrics-card flagged-metrics">
                    <div className="card-header">
                        <Flag className="card-icon" />
                        <h3>Flagged Reviews</h3>
                    </div>
                    <div className="card-content">
                        <div className="stat-row">
                            <span className="stat-label">Total Flagged:</span>
                            <span className="stat-value highlight-warning">
                                {metrics?.flaggedMetrics?.totalFlagged || 0}
                            </span>
                        </div>
                        
                        {metrics?.flaggedMetrics?.flagReasons && Object.keys(metrics.flaggedMetrics.flagReasons).length > 0 && (
                            <div className="flag-reasons">
                                <h4>Flag Reasons:</h4>
                                {Object.entries(metrics?.flaggedMetrics?.flagReasons || {}).map(([reason, count]) => (
                                    <div key={reason} className="reason-item">
                                        <span className="reason-text">{reason}</span>
                                        <span className="reason-count">{count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {metrics?.flaggedMetrics?.flaggedReviews?.length > 0 && (
                            <div className="recent-flags">
                                <h4>Recent Flagged Reviews:</h4>
                                <div className="flagged-list">
                                    {metrics?.flaggedMetrics?.flaggedReviews?.slice(0, 5).map(review => (
                                        <div key={review.id} className="flagged-item">
                                            <div className="reviewer-name">
                                                {anonymizeName(review.reviewerName || 'Anonymous')}
                                            </div>
                                            <div className="flag-details">
                                                <span className="location">{anonymizeCompanyName(review.locationTitle)}</span>
                                                <span className="rating">{'★'.repeat(review.starRating)}</span>
                                                <span className="flag-reason">{review.flaggedReason}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Status Overview */}
                <div className="metrics-card status-overview">
                    <div className="card-header">
                        <CheckCircle className="card-icon" />
                        <h3>Response Status Overview</h3>
                    </div>
                    <div className="card-content">
                        {Object.entries(metrics?.statusOverview || {}).map(([status, count]) => (
                            <div key={status} className="status-row">
                                <span className="status-label">{status.charAt(0).toUpperCase() + status.slice(1)}:</span>
                                <span className={`status-count status-${status}`}>{count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Top Performers */}
                {metrics?.topPerformers?.length > 0 && (
                    <div className="metrics-card top-performers">
                        <div className="card-header">
                            <Award className="card-icon" />
                            <h3>Top Performers</h3>
                        </div>
                        <div className="card-content">
                            {metrics?.topPerformers?.map((performer, index) => (
                                <div key={performer.technician.id} className="performer-item">
                                    <div className="performer-rank">#{index + 1}</div>
                                    <div className="performer-details">
                                        <div className="performer-name">
                                            {anonymizeName(performer.technician.name)} ({performer.technician.crmCode})
                                        </div>
                                        <div className="performer-stats">
                                            <span>Avg: {formatHours(performer.avgApprovalTime)}</span>
                                            <span>Rate: {Math.round(performer.fastApprovalRate)}%</span>
                                            <span>Total: {performer.totalApprovals}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </div>
            )}

            {/* Technician View - Individual Performance */}
            {viewMode === 'technician' && metrics && (
                <div className="metrics-grid">
                    {/* Assignment Metrics */}
                    <div className="metrics-card assignment-metrics">
                        <div className="card-header">
                            <User className="card-icon" />
                            <h3>Assignment & Response Metrics</h3>
                        </div>
                        <div className="card-content">
                            <div className="stat-row">
                                <span className="stat-label">Total Assigned:</span>
                                <span className="stat-value">{metrics?.assignmentMetrics?.totalAssigned || 0}</span>
                            </div>
                            <div className="stat-row">
                                <span className="stat-label">Responses Generated:</span>
                                <span className="stat-value highlight-good">
                                    {metrics?.assignmentMetrics?.totalGenerated || 0}
                                </span>
                            </div>
                            <div className="stat-row">
                                <span className="stat-label">Response Rate:</span>
                                <span className="stat-value">
                                    {Math.round(metrics?.assignmentMetrics?.responseRate || 0)}%
                                </span>
                            </div>
                            <div className="stat-row">
                                <span className="stat-label">Avg Response Length:</span>
                                <span className="stat-value">
                                    {metrics?.assignmentMetrics?.avgResponseLength || 0} chars
                                </span>
                            </div>
                            {metrics?.assignmentMetrics?.totalApproved > 0 && (
                                <div className="stat-row">
                                    <span className="stat-label">Approved as Manager:</span>
                                    <span className="stat-value highlight-good">
                                        {metrics?.assignmentMetrics?.totalApproved || 0}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Performance Trend Chart */}
                    <div className="metrics-card performance-trend">
                        <div className="card-header">
                            <BarChart3 className="card-icon" />
                            <h3>Performance Over Time</h3>
                        </div>
                        <div className="card-content">
                            {metrics?.weeklyPerformance && metrics.weeklyPerformance.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={metrics.weeklyPerformance}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="week" 
                                            tick={{ fontSize: 12 }}
                                        />
                                        <YAxis 
                                            hide={true}
                                            yAxisId="left"
                                        />
                                        <YAxis 
                                            orientation="right"
                                            hide={true}
                                            yAxisId="right"
                                            domain={[0, 100]}
                                        />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#fff', 
                                                border: '1px solid #ccc',
                                                borderRadius: '4px'
                                            }}
                                        />
                                        <Legend />
                                        <Line 
                                            type="monotone" 
                                            dataKey="assignedCount" 
                                            stroke="#42a5f5" 
                                            strokeWidth={2}
                                            name="Assigned"
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 6 }}
                                            yAxisId="left"
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="generatedCount" 
                                            stroke="#66bb6a" 
                                            strokeWidth={2}
                                            name="Generated"
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 6 }}
                                            yAxisId="left"
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="responseRate" 
                                            stroke="#ff9800" 
                                            strokeWidth={2}
                                            name="Response Rate (%)"
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 6 }}
                                            yAxisId="right"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="no-data-message">
                                    <p>No performance data available for the selected time range.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="metrics-card recent-activity">
                        <div className="card-header">
                            <Clock className="card-icon" />
                            <h3>Recent Assignments</h3>
                        </div>
                        <div className="card-content">
                            <div className="activity-list">
                                {metrics?.recentActivity?.recentAssignments?.slice(0, 5).map(assignment => (
                                    <div
                                        key={assignment.id}
                                        className="activity-item clickable"
                                        onClick={() => handleReviewClick(assignment.id)}
                                    >
                                        <div className="activity-main">
                                            <div className="reviewer-name">
                                                {anonymizeName(assignment.reviewerName || 'Anonymous')}
                                            </div>
                                            <div className="activity-details">
                                                <span className="location">{anonymizeCompanyName(assignment.locationTitle)}</span>
                                                <span className="rating">{'★'.repeat(assignment.starRating)}</span>
                                                <span className="status">{assignment.responseStatus}</span>
                                            </div>
                                        </div>
                                        <div className="activity-date">
                                            {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleDateString() : 'N/A'}
                                        </div>
                                    </div>
                                ))}
                                {(!metrics?.recentActivity?.recentAssignments || metrics?.recentActivity?.recentAssignments?.length === 0) && (
                                    <div className="no-data-message">
                                        <p>No recent assignments found.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Recent Responses */}
                    <div className="metrics-card recent-responses">
                        <div className="card-header">
                            <Award className="card-icon" />
                            <h3>Recent Responses</h3>
                        </div>
                        <div className="card-content">
                            <div className="response-list">
                                {metrics?.recentActivity?.recentResponses?.slice(0, 5).map(response => (
                                    <div
                                        key={response.id}
                                        className="response-item clickable"
                                        onClick={() => handleReviewClick(response.id)}
                                    >
                                        <div className="response-main">
                                            <div className="reviewer-name">
                                                {anonymizeName(response.reviewerName || 'Anonymous')}
                                            </div>
                                            <div className="response-details">
                                                <span className="location">{anonymizeCompanyName(response.locationTitle)}</span>
                                                <span className="rating">{'★'.repeat(response.starRating)}</span>
                                                <span className="length">{response.responseLength} chars</span>
                                            </div>
                                        </div>
                                        <div className="response-date">
                                            {response.generatedAt ? new Date(response.generatedAt).toLocaleDateString() : 'N/A'}
                                        </div>
                                    </div>
                                ))}
                                {(!metrics?.recentActivity?.recentResponses || metrics?.recentActivity?.recentResponses?.length === 0) && (
                                    <div className="no-data-message">
                                        <p>No recent responses found.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Review Details Modal */}
            {showReviewModal && selectedReview && (
                <div className="review-modal-overlay" onClick={closeReviewModal}>
                    <div className="review-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Review Details</h2>
                            <button className="close-button" onClick={closeReviewModal}>
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-content">
                            <div className="review-section">
                                <div className="reviewer-info">
                                    <h3>{anonymizeName(selectedReview.reviewerName || 'Anonymous')}</h3>
                                    <div className="rating-date">
                                        <span className="stars">{'★'.repeat(selectedReview.starRating || 0)}</span>
                                        <span className="date">
                                            {selectedReview.createTime ? new Date(selectedReview.createTime).toLocaleDateString() : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="review-content">
                                    <h4><MessageSquare size={18} /> Review Comment</h4>
                                    <p>{selectedReview.comment || 'No comment provided'}</p>
                                </div>

                                {selectedReview.responseText && (
                                    <div className="response-content">
                                        <h4><MessageSquare size={18} /> Response</h4>
                                        <p>{selectedReview.responseText}</p>
                                        <div className="response-meta">
                                            <span>Status: {selectedReview.responseStatus}</span>
                                            {selectedReview.responseDate && (
                                                <span>Responded: {new Date(selectedReview.responseDate).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="review-metadata">
                                    <h4>Additional Information</h4>
                                    <div className="metadata-grid">
                                        <div className="metadata-item">
                                            <span className="label">Location:</span>
                                            <span className="value">{anonymizeCompanyName(selectedReview.locationTitle || 'N/A')}</span>
                                        </div>
                                        <div className="metadata-item">
                                            <span className="label">Processing Status:</span>
                                            <span className="value">{selectedReview.processingStatus || 'N/A'}</span>
                                        </div>
                                        {selectedReview.assignedTechnician && (
                                            <div className="metadata-item">
                                                <span className="label">Assigned To:</span>
                                                <span className="value">{anonymizeName(selectedReview.assignedTechnician.name)}</span>
                                            </div>
                                        )}
                                        {selectedReview.approvedBy && (
                                            <div className="metadata-item">
                                                <span className="label">Approved By:</span>
                                                <span className="value">{anonymizeName(selectedReview.approvedByTechnician?.name || `User ${selectedReview.approvedBy}`)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {selectedReview.flaggedReason && (
                                    <div className="flagged-info">
                                        <h4><Flag size={18} /> Flagged Review</h4>
                                        <p>{selectedReview.flaggedReason}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PerformanceMetrics;